import React, { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material'
import {
  CheckCircle,
  RadioButtonUnchecked,
  Phone,
  Message,
  CalendarToday,
  Payment,
  Notifications,
  Close,
  Assignment,
  Person,
  Schedule,
  Done,
  Cancel,
} from '@mui/icons-material'
import { useFollowupChecklist } from '../hooks/useFollowupChecklist'
import { useFollowupJourney } from '../hooks/useFollowupJourney'
import { useFollowups } from '../hooks/useFollowups'
import dayjs from 'dayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

const STEP_KEYS = {
  TRIAGE: 'TRIAGE',
  ASSIGN: 'ASSIGN',
  CONTACT: 'CONTACT',
  SCHEDULE: 'SCHEDULE',
  PAYMENT: 'PAYMENT',
  REMINDER: 'REMINDER',
  CLOSE: 'CLOSE',
}

const STEP_CONFIG = {
  [STEP_KEYS.TRIAGE]: {
    label: 'Triage and Acknowledge',
    icon: <Assignment />,
    target: 'Case is understood and ready for assignment',
    color: 'info',
  },
  [STEP_KEYS.ASSIGN]: {
    label: 'Assign Therapist',
    icon: <Person />,
    target: 'Therapist is assigned and recorded',
    color: 'primary',
  },
  [STEP_KEYS.CONTACT]: {
    label: 'Contact Patient',
    icon: <Phone />,
    target: 'Contact outcome recorded + next step scheduled',
    color: 'warning',
  },
  [STEP_KEYS.SCHEDULE]: {
    label: 'Schedule Session',
    icon: <CalendarToday />,
    target: 'Confirmed slot recorded',
    color: 'secondary',
  },
  [STEP_KEYS.PAYMENT]: {
    label: 'Payment and Confirmation',
    icon: <Payment />,
    target: 'Payment confirmed and session marked confirmed',
    color: 'success',
  },
  [STEP_KEYS.REMINDER]: {
    label: 'Reminders',
    icon: <Notifications />,
    target: 'Patient receives reminders',
    color: 'info',
  },
  [STEP_KEYS.CLOSE]: {
    label: 'Close Loop',
    icon: <Done />,
    target: 'Session completed or closed with reason',
    color: 'default',
  },
}

export default function FollowupChecklist({ intervention, onRefresh }) {
  const [activeStep, setActiveStep] = useState(null)
  const [stepNotes, setStepNotes] = useState({})
  const [offlineMode, setOfflineMode] = useState({})
  const [followupAt, setFollowupAt] = useState({})
  const [nextFollowupAt, setNextFollowupAt] = useState({})
  const [contactOutcome, setContactOutcome] = useState({})
  const [therapistName, setTherapistName] = useState('')
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false)
  const [closeDialogOpen, setCloseDialogOpen] = useState(false)
  const [closeReason, setCloseReason] = useState('completed')

  // Determine action track
  const hasTherapy = intervention?.therapy?.prescribedTherapy?.length > 0 ||
    intervention?.therapy?.therapiesNeeded?.length > 0 ||
    intervention?.therapy?.prescribedActions?.length > 0 ||
    intervention?.therapy?.suggestedBookingTherapy

  const hasAssessment = intervention?.assessment?.prescribedAssessments?.length > 0 ||
    intervention?.assessment?.psychAssessments?.length > 0

  const actionTrack = hasTherapy ? 'therapy' : hasAssessment ? 'assessment' : 'therapy'

  const { checklist, loading: checklistLoading } = useFollowupChecklist(actionTrack, true)
  const { journey, loading: journeyLoading, refreshJourney } = useFollowupJourney(
    intervention?.bookedEventId,
    actionTrack,
    true
  )
  const { addFollowup, refreshFollowups } = useFollowups(
    intervention?.bookedEventId,
    actionTrack,
    true
  )

  // Determine current stage and step statuses
  const currentStage = journey?.currentStageKey || null
  const stepStatuses = journey?.stepStatuses || {}

  // Auto-focus on current stage or first incomplete step
  React.useEffect(() => {
    if (currentStage && !activeStep) {
      setActiveStep(currentStage)
    } else if (!currentStage && !activeStep) {
      setActiveStep(STEP_KEYS.TRIAGE)
    }
  }, [currentStage, activeStep])

  const handleStepClick = (stepKey) => {
    setActiveStep(stepKey)
  }

  const getStepStatus = (stepKey) => {
    const status = stepStatuses[stepKey]
    if (!status) return 'pending'
    if (status.status === 'done') return 'completed'
    if (status.status === 'pending') return 'in_progress'
    return 'pending'
  }

  const getStepStatusIcon = (stepKey) => {
    const status = getStepStatus(stepKey)
    if (status === 'completed') {
      return <CheckCircle color="success" />
    }
    if (status === 'in_progress') {
      return <RadioButtonUnchecked color="primary" />
    }
    return <RadioButtonUnchecked color="disabled" />
  }

  const handleLogFollowup = async (stepKey, note, status = 'done', nextFollowup = null) => {
    const isOffline = offlineMode[stepKey] || false
    const customFollowupAt = followupAt[stepKey] || null

    const followupData = {
      note: `STEP:${stepKey} - ${note}`,
      stepKey,
      status,
      followupMode: isOffline ? 'manual' : 'manual',
      followupAt: customFollowupAt ? customFollowupAt.toISOString() : undefined,
      nextFollowupAt: nextFollowup ? nextFollowup.toISOString() : undefined,
    }

    const result = await addFollowup(followupData)
    if (result.success) {
      await refreshJourney()
      await refreshFollowups()
      if (onRefresh) onRefresh()
      
      // Clear step state
      setStepNotes({ ...stepNotes, [stepKey]: '' })
      setOfflineMode({ ...offlineMode, [stepKey]: false })
      setFollowupAt({ ...followupAt, [stepKey]: null })
      setNextFollowupAt({ ...nextFollowupAt, [stepKey]: null })
    }
    return result
  }

  // Step 1: Triage
  const handleTriage = async (action) => {
    const note = action === 'triaged' 
      ? 'triaged - case reviewed and ready for assignment'
      : 'clarification requested - missing details need to be obtained'
    
    await handleLogFollowup(STEP_KEYS.TRIAGE, note)
  }

  // Step 2: Assign Therapist
  const handleAssignTherapist = async () => {
    if (!therapistName.trim()) {
      return
    }
    const note = `assigned to ${therapistName}`
    await handleLogFollowup(STEP_KEYS.ASSIGN, note)
    setAssignDialogOpen(false)
    setTherapistName('')
  }

      // Step 3: Contact Patient
  const handleContactAction = async (action, outcome = null) => {
    let note = ''
    let status = 'done'
    let nextFollowup = null

    switch (action) {
      case 'call':
        note = 'call attempted'
        break
      case 'whatsapp':
        note = 'WhatsApp message sent'
        break
      case 'sms':
        note = 'SMS sent'
        break
      case 'log':
        const outcomeValue = outcome || contactOutcome[STEP_KEYS.CONTACT] || 'attempted'
        note = `contact attempt logged - outcome: ${outcomeValue}`
        // Set pending status and next followup for cases that need retry
        if (outcomeValue === 'no-answer' || outcomeValue === 'reached-info' || outcomeValue === 'reached-different') {
          status = 'pending'
          nextFollowup = dayjs().add(1, 'day')
        }
        break
      default:
        return
    }

    await handleLogFollowup(STEP_KEYS.CONTACT, note, status, nextFollowup)
    if (action === 'log') {
      setContactOutcome({ ...contactOutcome, [STEP_KEYS.CONTACT]: '' })
    }
  }

  // Step 4: Schedule Session
  const handleScheduleAction = async (action) => {
    let note = ''
    let status = 'done'

    switch (action) {
      case 'create':
        note = 'booking slot created'
        break
      case 'send_link':
        note = 'booking link sent to patient'
        break
      case 'confirm':
        note = 'slot confirmed by patient'
        break
      default:
        return
    }

    await handleLogFollowup(STEP_KEYS.SCHEDULE, note, status)
    setScheduleDialogOpen(false)
  }

  // Step 5: Payment
  const handlePaymentAction = async (action) => {
    let note = ''
    let status = action === 'confirmed' ? 'done' : 'pending'

    if (action === 'send_link') {
      note = 'payment link sent to patient'
    } else if (action === 'confirmed') {
      note = 'payment confirmed and session marked confirmed'
    }

    await handleLogFollowup(STEP_KEYS.PAYMENT, note, status)
  }

  // Step 6: Reminders
  const handleReminder = async () => {
    const note = 'reminder sent to patient'
    await handleLogFollowup(STEP_KEYS.REMINDER, note)
  }

  // Step 7: Close Loop
  const handleClose = async () => {
    const note = `closed - ${closeReason}`
    await handleLogFollowup(STEP_KEYS.CLOSE, note)
    setCloseDialogOpen(false)
    setCloseReason('completed')
  }

  const renderStepContent = (stepKey) => {
    const config = STEP_CONFIG[stepKey]
    const status = getStepStatus(stepKey)
    const isActive = activeStep === stepKey

    if (!isActive) return null

    return (
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mt: 2,
          border: `2px solid ${
            status === 'completed'
              ? 'success.main'
              : status === 'in_progress'
              ? 'primary.main'
              : 'divider'
          }`,
        }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {config.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Target: {config.target}
            </Typography>
          </Box>

          <Divider />

          {/* Step-specific content */}
          {stepKey === STEP_KEYS.TRIAGE && (
            <Stack spacing={2}>
              <Alert severity="info">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Step 1: Acknowledge the trigger
                </Typography>
                <Typography variant="body2">
                  Target: Trigger is triaged and ready for assignment.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Read the trigger note and patient summary<br />
                  • Confirm patient identity and clinic location<br />
                  • If details are missing, pause and request clarification from the referring practitioner
                </Typography>
              </Alert>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  onClick={() => handleTriage('triaged')}
                  startIcon={<CheckCircle />}
                >
                  Mark Triaged
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleTriage('clarification')}
                  startIcon={<Cancel />}
                >
                  Request Clarification
                </Button>
              </Stack>
            </Stack>
          )}

          {stepKey === STEP_KEYS.ASSIGN && (
            <Stack spacing={2}>
              <Alert severity="info">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Step 2: Assign therapist
                </Typography>
                <Typography variant="body2">
                  Target: Therapist is assigned and recorded.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Use the suggested therapist if present and available<br />
                  • If not, assign based on availability and patient preferences (gender/language/time)<br />
                  • Record the assignment
                </Typography>
              </Alert>
              <Button
                variant="contained"
                onClick={() => setAssignDialogOpen(true)}
                startIcon={<Person />}
              >
                Assign Therapist
              </Button>
            </Stack>
          )}

          {stepKey === STEP_KEYS.CONTACT && (
            <Stack spacing={2}>
              <Alert severity="info">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Step 3: Contact patient (call first, then WhatsApp/SMS fallback)
                </Typography>
                <Typography variant="body2">
                  Target: Patient contact outcome recorded with a clear next step.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Call attempt ladder:</strong><br />
                  1) Attempt 1: call within 24 hours of trigger. If no pickup: leave voicemail if allowed, then send WhatsApp/SMS.<br />
                  2) Attempt 2: call next business day at a different time. If no pickup: send WhatsApp follow-up.<br />
                  3) Attempt 3: call within 48-72 hours. If no pickup: send final WhatsApp/SMS and mark as unreachable.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>If patient picks up:</strong> Confirm interest, preferred time window. If ready, move to scheduling. If not, set follow-up time.<br />
                  <strong>If no pickup:</strong> Always fall back to WhatsApp or SMS with a short, clear ask.
                </Typography>
              </Alert>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  startIcon={<Phone />}
                  href={intervention?.patient?.mobile ? `tel:${intervention.patient.mobile}` : undefined}
                  onClick={() => handleContactAction('call')}
                  disabled={!intervention?.patient?.mobile}
                >
                  Call Now
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<Message />}
                  href={intervention?.patient?.mobile ? `https://wa.me/${intervention.patient.mobile.replace(/[^0-9]/g, '')}` : undefined}
                  target="_blank"
                  onClick={() => handleContactAction('whatsapp')}
                  disabled={!intervention?.patient?.mobile}
                >
                  Send WhatsApp
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Message />}
                  href={intervention?.patient?.mobile ? `sms:${intervention.patient.mobile}` : undefined}
                  onClick={() => handleContactAction('sms')}
                  disabled={!intervention?.patient?.mobile}
                >
                  Send SMS
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleContactAction('log')}
                >
                  Log Attempt
                </Button>
              </Stack>
              {!intervention?.patient?.mobile && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Patient mobile number not available. Please log the contact attempt manually.
                </Alert>
              )}
              <FormControl fullWidth>
                <InputLabel>Contact Outcome</InputLabel>
                <Select
                  value={contactOutcome[STEP_KEYS.CONTACT] || ''}
                  onChange={(e) => setContactOutcome({ ...contactOutcome, [STEP_KEYS.CONTACT]: e.target.value })}
                  label="Contact Outcome"
                >
                  <MenuItem value="reached-ready">Reached and ready to proceed</MenuItem>
                  <MenuItem value="reached-info">Reached but wants more information</MenuItem>
                  <MenuItem value="reached-different">Reached but needs different therapist/time</MenuItem>
                  <MenuItem value="reached-declined">Reached but declines therapy</MenuItem>
                  <MenuItem value="reached-financial">Reached but financial constraint</MenuItem>
                  <MenuItem value="reached-guardian">Reached but needs guardian/alternate contact</MenuItem>
                  <MenuItem value="no-answer">No answer / voicemail / unread WhatsApp</MenuItem>
                  <MenuItem value="wrong-number">Wrong or invalid number</MenuItem>
                  <MenuItem value="language-barrier">Language barrier</MenuItem>
                  <MenuItem value="safety-concern">Safety/clinical concern raised</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}

          {stepKey === STEP_KEYS.SCHEDULE && (
            <Stack spacing={2}>
              <Alert severity="info">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Step 4: Schedule the session
                </Typography>
                <Typography variant="body2">
                  Target: A confirmed slot is agreed and recorded.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Offer 2-3 time options<br />
                  • Confirm time zone and expected duration<br />
                  • Send booking/payment link and confirm receipt
                </Typography>
              </Alert>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  onClick={() => setScheduleDialogOpen(true)}
                  startIcon={<CalendarToday />}
                >
                  Create Booking
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleScheduleAction('send_link')}
                  startIcon={<Message />}
                >
                  Send Booking Link
                </Button>
              </Stack>
            </Stack>
          )}

          {stepKey === STEP_KEYS.PAYMENT && (
            <Stack spacing={2}>
              <Alert severity="info">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Step 5: Payment and confirmation
                </Typography>
                <Typography variant="body2">
                  Target: Payment confirmed and session marked as confirmed.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Verify payment status<br />
                  • Send confirmation message and calendar reminder if possible
                </Typography>
              </Alert>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  onClick={() => handlePaymentAction('send_link')}
                  startIcon={<Payment />}
                >
                  Send Payment Link
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handlePaymentAction('confirmed')}
                  startIcon={<CheckCircle />}
                >
                  Mark Payment Received
                </Button>
              </Stack>
            </Stack>
          )}

          {stepKey === STEP_KEYS.REMINDER && (
            <Stack spacing={2}>
              <Alert severity="info">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Step 6: Day-of reminders
                </Typography>
                <Typography variant="body2">
                  Target: Patient is reminded and session attendance is likely.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Send a reminder 24 hours and 2 hours before the session<br />
                  • If patient responds with conflict, reschedule and return to Step 4
                </Typography>
              </Alert>
              <Button
                variant="contained"
                onClick={handleReminder}
                startIcon={<Notifications />}
              >
                Send Reminder
              </Button>
            </Stack>
          )}

          {stepKey === STEP_KEYS.CLOSE && (
            <Stack spacing={2}>
              <Alert severity="info">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Step 7: Close the loop
                </Typography>
                <Typography variant="body2">
                  Target: Session completed or closed with a recorded outcome.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • If attended, mark completed<br />
                  • If no-show, follow the no-show protocol and reschedule<br />
                  • If patient declines, record the reason and close the action
                </Typography>
              </Alert>
              <Button
                variant="contained"
                onClick={() => setCloseDialogOpen(true)}
                startIcon={<Done />}
              >
                Close Loop
              </Button>
            </Stack>
          )}

          {/* Common offline logging section */}
          <Divider />
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={offlineMode[stepKey] || false}
                  onChange={(e) => setOfflineMode({ ...offlineMode, [stepKey]: e.target.checked })}
                />
              }
              label="Performed offline (action done outside system)"
            />
            {offlineMode[stepKey] && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Actual Followup Date"
                  value={followupAt[stepKey] || null}
                  onChange={(date) => setFollowupAt({ ...followupAt, [stepKey]: date })}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </LocalizationProvider>
            )}
            <TextField
              label="Additional Notes (optional)"
              multiline
              rows={3}
              fullWidth
              value={stepNotes[stepKey] || ''}
              onChange={(e) => setStepNotes({ ...stepNotes, [stepKey]: e.target.value })}
              size="small"
            />
          </Stack>
        </Stack>
      </Paper>
    )
  }

  if (checklistLoading || journeyLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          Followup Checklist
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Interactive step-by-step guidance to ensure patients reach a successful first therapy session.
        </Typography>
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600}>
            Success Criteria:
          </Typography>
          <Typography variant="body2">
            Patient attends the first therapy session and the action is closed.
          </Typography>
        </Alert>

        {currentStage && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Current Stage: <strong>{STEP_CONFIG[currentStage]?.label || currentStage}</strong>
            {journey?.currentStageStatus && (
              <Chip
                label={journey.currentStageStatus}
                size="small"
                color={journey.currentStageStatus === 'done' ? 'success' : 'default'}
                sx={{ ml: 1 }}
              />
            )}
          </Alert>
        )}

        <Stack spacing={2}>
          {Object.values(STEP_KEYS).map((stepKey) => {
            const config = STEP_CONFIG[stepKey]
            const status = getStepStatus(stepKey)
            const isActive = activeStep === stepKey

            return (
              <Box key={stepKey}>
                <Paper
                  elevation={isActive ? 4 : 1}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: `2px solid ${
                      isActive
                        ? 'primary.main'
                        : status === 'completed'
                        ? 'success.main'
                        : 'transparent'
                    }`,
                    backgroundColor: isActive ? 'action.selected' : 'background.paper',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onClick={() => handleStepClick(stepKey)}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getStepStatusIcon(stepKey)}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {config.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {config.target}
                      </Typography>
                    </Box>
                    <Chip
                      label={status === 'completed' ? 'Done' : status === 'in_progress' ? 'In Progress' : 'Pending'}
                      size="small"
                      color={status === 'completed' ? 'success' : status === 'in_progress' ? 'primary' : 'default'}
                    />
                    {stepStatuses[stepKey]?.lastNote && (
                      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {stepStatuses[stepKey].lastNote}
                      </Typography>
                    )}
                  </Stack>
                </Paper>
                {renderStepContent(stepKey)}
              </Box>
            )
          })}
        </Stack>

        {/* Assign Therapist Dialog */}
        <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Assign Therapist</DialogTitle>
          <DialogContent>
            <TextField
              label="Therapist Name"
              fullWidth
              value={therapistName}
              onChange={(e) => setTherapistName(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAssignTherapist}
              variant="contained"
              disabled={!therapistName.trim()}
            >
              Assign
            </Button>
          </DialogActions>
        </Dialog>

        {/* Schedule Dialog */}
        <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Schedule Session</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <DatePicker
                label="Session Date"
                slotProps={{ textField: { fullWidth: true } }}
              />
              <TimePicker
                label="Session Time"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => handleScheduleAction('create')} variant="contained">
              Create Booking
            </Button>
          </DialogActions>
        </Dialog>

        {/* Close Dialog */}
        <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Close Loop</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Outcome</InputLabel>
              <Select
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                label="Outcome"
              >
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="no-show">No Show</MenuItem>
                <MenuItem value="declined">Declined</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCloseDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  )
}

